import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';
import {
  Shield, ArrowLeft, Users, DollarSign, TrendingUp, AlertTriangle, FileText,
  Upload, CheckCircle, Clock, XCircle, Calendar, BarChart3, FolderOpen, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

// ==================== Mock Data ====================

const MOCK_FUNDS: Record<string, any> = {
  'fund-001': {
    id: 'fund-001',
    name: 'Smith Family Super Fund',
    abn: '12 345 678 901',
    tfn: '123 456 789',
    establishmentDate: '15/03/2010',
    trusteeType: 'Individual',
    totalAssets: 1_850_000,
    contributionsYTD: 54_000,
    complianceStatus: 'green',
    trustees: [
      { name: 'John Smith', type: 'Individual', role: 'Trustee', dateAppointed: '15/03/2010', declarationStatus: 'Current' },
      { name: 'Sarah Smith', type: 'Individual', role: 'Trustee', dateAppointed: '15/03/2010', declarationStatus: 'Current' },
    ],
    members: [
      { name: 'John Smith', dob: '12/05/1965', tfn: '***-***-789', preservationAge: 60, status: 'Pension', balance: 1_120_000 },
      { name: 'Sarah Smith', dob: '23/08/1968', tfn: '***-***-456', preservationAge: 60, status: 'Accumulation', balance: 730_000 },
    ],
    trustDeed: {
      currentVersion: 'v4.2',
      lastUpdated: '01/07/2023',
      provider: 'DBA Lawyers',
      reviewDue: '01/07/2026',
      history: [
        { version: 'v4.2', date: '01/07/2023', description: 'Updated for SIS Act amendments' },
        { version: 'v4.1', date: '15/01/2022', description: 'Updated for LRBA provisions' },
        { version: 'v4.0', date: '01/07/2020', description: 'Major review and update' },
        { version: 'v3.0', date: '01/07/2017', description: 'Transfer balance cap amendments' },
      ],
    },
    bdbn: [
      { member: 'John Smith', type: 'Binding', beneficiaries: 'Sarah Smith (100%)', dateSigned: '15/06/2024', expiry: '15/06/2027', daysUntilExpiry: 425 },
      { member: 'Sarah Smith', type: 'Non-Binding', beneficiaries: 'John Smith (70%), Children (30%)', dateSigned: '15/06/2024', expiry: 'N/A', daysUntilExpiry: null },
    ],
    investmentStrategy: {
      lastReviewed: '01/07/2025',
      nextReview: '01/07/2026',
      allocations: [
        { asset: 'Australian Shares', target: 35, actual: 38 },
        { asset: 'International Shares', target: 20, actual: 18 },
        { asset: 'Property', target: 25, actual: 24 },
        { asset: 'Fixed Interest', target: 10, actual: 12 },
        { asset: 'Cash', target: 10, actual: 8 },
      ],
    },
    lodgements: [
      { year: '2024-25', dueDate: '28/02/2026', status: 'not-started' },
      { year: '2023-24', dueDate: '28/02/2025', status: 'lodged' },
      { year: '2022-23', dueDate: '28/02/2024', status: 'lodged' },
      { year: '2021-22', dueDate: '28/02/2023', status: 'lodged' },
    ],
    auditPack: [
      { category: 'Bank Reconciliation', items: 4, completed: 4 },
      { category: 'Asset Valuations', items: 3, completed: 3 },
      { category: 'Contributions', items: 5, completed: 4 },
      { category: 'Pension Records', items: 3, completed: 2 },
      { category: 'Trust Deed', items: 2, completed: 2 },
      { category: 'Investment Strategy', items: 2, completed: 2 },
      { category: 'Minutes', items: 4, completed: 3 },
      { category: 'Member Statements', items: 2, completed: 1 },
    ],
  },
  'fund-002': {
    id: 'fund-002',
    name: 'Johnson Retirement Fund',
    abn: '98 765 432 109',
    tfn: '987 654 321',
    establishmentDate: '01/07/2008',
    trusteeType: 'Corporate',
    totalAssets: 2_340_000,
    contributionsYTD: 81_000,
    complianceStatus: 'amber',
    trustees: [
      { name: 'Johnson Family Pty Ltd', type: 'Corporate', role: 'Corporate Trustee', dateAppointed: '01/07/2008', declarationStatus: 'Current' },
      { name: 'Robert Johnson', type: 'Individual', role: 'Director', dateAppointed: '01/07/2008', declarationStatus: 'Current' },
      { name: 'Mary Johnson', type: 'Individual', role: 'Director', dateAppointed: '01/07/2008', declarationStatus: 'Overdue' },
    ],
    members: [
      { name: 'Robert Johnson', dob: '04/11/1958', tfn: '***-***-321', preservationAge: 58, status: 'Pension', balance: 1_450_000 },
      { name: 'Mary Johnson', dob: '19/02/1960', tfn: '***-***-654', preservationAge: 59, status: 'Pension', balance: 620_000 },
      { name: 'David Johnson', dob: '08/09/1988', tfn: '***-***-987', preservationAge: 60, status: 'Accumulation', balance: 270_000 },
    ],
    trustDeed: {
      currentVersion: 'v3.1',
      lastUpdated: '15/11/2022',
      provider: 'Cleardocs',
      reviewDue: '15/11/2025',
      history: [
        { version: 'v3.1', date: '15/11/2022', description: 'Annual compliance update' },
        { version: 'v3.0', date: '01/07/2020', description: 'Major review' },
        { version: 'v2.0', date: '01/07/2017', description: 'Transfer balance cap amendments' },
      ],
    },
    bdbn: [
      { member: 'Robert Johnson', type: 'Binding', beneficiaries: 'Mary Johnson (100%)', dateSigned: '01/03/2024', expiry: '01/03/2027', daysUntilExpiry: 318 },
      { member: 'Mary Johnson', type: 'Binding', beneficiaries: 'Robert Johnson (60%), David Johnson (40%)', dateSigned: '10/01/2024', expiry: '10/01/2027', daysUntilExpiry: 268 },
      { member: 'David Johnson', type: 'Non-Binding', beneficiaries: 'Estate', dateSigned: '01/06/2023', expiry: 'N/A', daysUntilExpiry: null },
    ],
    investmentStrategy: {
      lastReviewed: '01/07/2025',
      nextReview: '01/07/2026',
      allocations: [
        { asset: 'Australian Shares', target: 30, actual: 28 },
        { asset: 'International Shares', target: 25, actual: 27 },
        { asset: 'Property', target: 20, actual: 22 },
        { asset: 'Fixed Interest', target: 15, actual: 14 },
        { asset: 'Cash', target: 10, actual: 9 },
      ],
    },
    lodgements: [
      { year: '2024-25', dueDate: '28/02/2026', status: 'draft' },
      { year: '2023-24', dueDate: '28/02/2025', status: 'lodged' },
      { year: '2022-23', dueDate: '28/02/2024', status: 'lodged' },
    ],
    auditPack: [
      { category: 'Bank Reconciliation', items: 4, completed: 3 },
      { category: 'Asset Valuations', items: 3, completed: 2 },
      { category: 'Contributions', items: 5, completed: 3 },
      { category: 'Pension Records', items: 3, completed: 1 },
      { category: 'Trust Deed', items: 2, completed: 2 },
      { category: 'Investment Strategy', items: 2, completed: 1 },
      { category: 'Minutes', items: 4, completed: 2 },
      { category: 'Member Statements', items: 2, completed: 0 },
    ],
  },
  'fund-003': {
    id: 'fund-003',
    name: 'ABC Pty Ltd Superannuation Fund',
    abn: '11 222 333 444',
    tfn: '111 222 333',
    establishmentDate: '01/01/2018',
    trusteeType: 'Corporate',
    totalAssets: 520_000,
    contributionsYTD: 27_500,
    complianceStatus: 'red',
    trustees: [
      { name: 'ABC Pty Ltd', type: 'Corporate', role: 'Corporate Trustee', dateAppointed: '01/01/2018', declarationStatus: 'Overdue' },
      { name: 'Alice Brown', type: 'Individual', role: 'Director', dateAppointed: '01/01/2018', declarationStatus: 'Overdue' },
      { name: 'Chris Brown', type: 'Individual', role: 'Director', dateAppointed: '15/06/2019', declarationStatus: 'Overdue' },
    ],
    members: [
      { name: 'Alice Brown', dob: '30/04/1975', tfn: '***-***-333', preservationAge: 60, status: 'Accumulation', balance: 280_000 },
      { name: 'Chris Brown', dob: '14/12/1977', tfn: '***-***-222', preservationAge: 60, status: 'Accumulation', balance: 145_000 },
      { name: 'Emily Brown', dob: '22/06/1980', tfn: '***-***-111', preservationAge: 60, status: 'Accumulation', balance: 55_000 },
      { name: 'Mark Brown', dob: '08/03/1982', tfn: '***-***-444', preservationAge: 60, status: 'Accumulation', balance: 40_000 },
    ],
    trustDeed: {
      currentVersion: 'v2.0',
      lastUpdated: '01/07/2020',
      provider: 'SuperCentral',
      reviewDue: '01/07/2023',
      history: [
        { version: 'v2.0', date: '01/07/2020', description: 'Initial 3-year review' },
        { version: 'v1.0', date: '01/01/2018', description: 'Establishment deed' },
      ],
    },
    bdbn: [
      { member: 'Alice Brown', type: 'Binding', beneficiaries: 'Chris Brown (100%)', dateSigned: '01/06/2023', expiry: '01/06/2026', daysUntilExpiry: 45 },
      { member: 'Chris Brown', type: 'Binding', beneficiaries: 'Alice Brown (100%)', dateSigned: '01/02/2023', expiry: '01/02/2026', daysUntilExpiry: -75 },
      { member: 'Emily Brown', type: 'Non-Binding', beneficiaries: 'Estate', dateSigned: '15/03/2022', expiry: 'N/A', daysUntilExpiry: null },
      { member: 'Mark Brown', type: 'None', beneficiaries: 'N/A', dateSigned: 'N/A', expiry: 'N/A', daysUntilExpiry: null },
    ],
    investmentStrategy: {
      lastReviewed: '01/07/2024',
      nextReview: '01/07/2025',
      allocations: [
        { asset: 'Australian Shares', target: 40, actual: 45 },
        { asset: 'International Shares', target: 15, actual: 10 },
        { asset: 'Property', target: 20, actual: 25 },
        { asset: 'Fixed Interest', target: 15, actual: 12 },
        { asset: 'Cash', target: 10, actual: 8 },
      ],
    },
    lodgements: [
      { year: '2024-25', dueDate: '28/02/2026', status: 'not-started' },
      { year: '2023-24', dueDate: '28/02/2025', status: 'overdue' },
      { year: '2022-23', dueDate: '28/02/2024', status: 'lodged' },
    ],
    auditPack: [
      { category: 'Bank Reconciliation', items: 4, completed: 1 },
      { category: 'Asset Valuations', items: 3, completed: 0 },
      { category: 'Contributions', items: 5, completed: 2 },
      { category: 'Pension Records', items: 3, completed: 0 },
      { category: 'Trust Deed', items: 2, completed: 1 },
      { category: 'Investment Strategy', items: 2, completed: 0 },
      { category: 'Minutes', items: 4, completed: 1 },
      { category: 'Member Statements', items: 2, completed: 0 },
    ],
  },
  'fund-004': {
    id: 'fund-004',
    name: 'Garcia & Partners Super Fund',
    abn: '55 666 777 888',
    tfn: '556 667 778',
    establishmentDate: '01/10/2015',
    trusteeType: 'Individual',
    totalAssets: 980_000,
    contributionsYTD: 36_000,
    complianceStatus: 'green',
    trustees: [
      { name: 'Carlos Garcia', type: 'Individual', role: 'Trustee', dateAppointed: '01/10/2015', declarationStatus: 'Current' },
      { name: 'Maria Garcia', type: 'Individual', role: 'Trustee', dateAppointed: '01/10/2015', declarationStatus: 'Current' },
    ],
    members: [
      { name: 'Carlos Garcia', dob: '17/07/1970', tfn: '***-***-778', preservationAge: 60, status: 'Accumulation', balance: 580_000 },
      { name: 'Maria Garcia', dob: '29/11/1972', tfn: '***-***-667', preservationAge: 60, status: 'Pension', balance: 400_000 },
    ],
    trustDeed: {
      currentVersion: 'v3.0',
      lastUpdated: '15/09/2024',
      provider: 'DBA Lawyers',
      reviewDue: '15/09/2027',
      history: [
        { version: 'v3.0', date: '15/09/2024', description: 'Updated for latest SIS amendments' },
        { version: 'v2.0', date: '01/07/2020', description: 'Major compliance update' },
        { version: 'v1.0', date: '01/10/2015', description: 'Establishment deed' },
      ],
    },
    bdbn: [
      { member: 'Carlos Garcia', type: 'Binding', beneficiaries: 'Maria Garcia (100%)', dateSigned: '01/09/2025', expiry: '01/09/2028', daysUntilExpiry: 868 },
      { member: 'Maria Garcia', type: 'Binding', beneficiaries: 'Carlos Garcia (70%), Children (30%)', dateSigned: '01/09/2025', expiry: '01/09/2028', daysUntilExpiry: 868 },
    ],
    investmentStrategy: {
      lastReviewed: '01/07/2025',
      nextReview: '01/07/2026',
      allocations: [
        { asset: 'Australian Shares', target: 30, actual: 32 },
        { asset: 'International Shares', target: 25, actual: 23 },
        { asset: 'Property', target: 15, actual: 16 },
        { asset: 'Fixed Interest', target: 20, actual: 19 },
        { asset: 'Cash', target: 10, actual: 10 },
      ],
    },
    lodgements: [
      { year: '2024-25', dueDate: '28/02/2026', status: 'draft' },
      { year: '2023-24', dueDate: '28/02/2025', status: 'lodged' },
      { year: '2022-23', dueDate: '28/02/2024', status: 'lodged' },
      { year: '2021-22', dueDate: '28/02/2023', status: 'lodged' },
    ],
    auditPack: [
      { category: 'Bank Reconciliation', items: 4, completed: 4 },
      { category: 'Asset Valuations', items: 3, completed: 3 },
      { category: 'Contributions', items: 5, completed: 5 },
      { category: 'Pension Records', items: 3, completed: 3 },
      { category: 'Trust Deed', items: 2, completed: 2 },
      { category: 'Investment Strategy', items: 2, completed: 2 },
      { category: 'Minutes', items: 4, completed: 4 },
      { category: 'Member Statements', items: 2, completed: 2 },
    ],
  },
};

const COMPLIANCE_LABEL: Record<string, { label: string; color: string }> = {
  green: { label: 'Compliant', color: 'bg-green-500' },
  amber: { label: 'Attention Needed', color: 'bg-amber-500' },
  red: { label: 'Non-Compliant', color: 'bg-red-500' },
};

const LODGEMENT_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  lodged: { label: 'Lodged', variant: 'default' },
  overdue: { label: 'Overdue', variant: 'destructive' },
  draft: { label: 'Draft', variant: 'secondary' },
  'not-started': { label: 'Not Started', variant: 'outline' },
};

// ==================== Component ====================

export default function SMSFFundDetailPage() {
  const { fundId } = useParams<{ fundId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Primary fund query
  const { data: apiFund, isLoading: fundLoading } = useQuery({
    queryKey: ['smsf-fund', fundId],
    queryFn: async () => {
      const r = await apiClient.get(`/smsf/funds/${fundId}`);
      return r.data.data;
    },
    enabled: !!fundId,
  });

  // Tab-specific queries
  const { data: apiTrustees } = useQuery({
    queryKey: ['smsf-fund-trustees', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/trustees`); return r.data.data; },
    enabled: !!fundId && activeTab === 'trustees',
  });

  const { data: apiMembers } = useQuery({
    queryKey: ['smsf-fund-members', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/members`); return r.data.data; },
    enabled: !!fundId && (activeTab === 'members' || activeTab === 'overview'),
  });

  const { data: apiTrustDeeds } = useQuery({
    queryKey: ['smsf-fund-trust-deeds', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/trust-deeds`); return r.data.data; },
    enabled: !!fundId && (activeTab === 'trust-deed' || activeTab === 'overview'),
  });

  const { data: apiBdbn } = useQuery({
    queryKey: ['smsf-fund-bdbn', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/bdbn`); return r.data.data; },
    enabled: !!fundId && activeTab === 'bdbn',
  });

  const { data: apiStrategies } = useQuery({
    queryKey: ['smsf-fund-strategies', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/strategies`); return r.data.data; },
    enabled: !!fundId && (activeTab === 'investment' || activeTab === 'overview'),
  });

  const { data: apiLodgements } = useQuery({
    queryKey: ['smsf-fund-lodgements', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/lodgements`); return r.data.data; },
    enabled: !!fundId && (activeTab === 'lodgement' || activeTab === 'overview'),
  });

  const { data: apiAudits } = useQuery({
    queryKey: ['smsf-fund-audits', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/audits`); return r.data.data; },
    enabled: !!fundId && (activeTab === 'audit-pack' || activeTab === 'overview'),
  });

  // Fall back to mock data keyed by fundId
  const mockFund = MOCK_FUNDS[fundId || ''];

  // Build merged fund object: API data takes precedence, mock as fallback
  const fund = apiFund ? {
    ...mockFund,
    id: apiFund.id,
    name: apiFund.fund_name || apiFund.name || mockFund?.name,
    abn: apiFund.abn || mockFund?.abn,
    tfn: apiFund.tfn || mockFund?.tfn,
    establishmentDate: apiFund.establishment_date || apiFund.establishmentDate || mockFund?.establishmentDate,
    trusteeType: apiFund.trustee_type || apiFund.trusteeType || mockFund?.trusteeType,
    totalAssets: apiFund.total_assets ?? apiFund.totalAssets ?? mockFund?.totalAssets ?? 0,
    contributionsYTD: apiFund.contributions_ytd ?? apiFund.contributionsYTD ?? mockFund?.contributionsYTD ?? 0,
    complianceStatus: apiFund.compliance_status || apiFund.complianceStatus || mockFund?.complianceStatus || 'green',
    trustees: apiTrustees || apiFund.trustees || mockFund?.trustees || [],
    members: apiMembers || apiFund.members || mockFund?.members || [],
    trustDeed: apiTrustDeeds || apiFund.trust_deed || apiFund.trustDeed || mockFund?.trustDeed || { currentVersion: '', lastUpdated: '', provider: '', reviewDue: '', history: [] },
    bdbn: apiBdbn || apiFund.bdbn || mockFund?.bdbn || [],
    investmentStrategy: apiStrategies || apiFund.investment_strategy || apiFund.investmentStrategy || mockFund?.investmentStrategy || { lastReviewed: '', nextReview: '', allocations: [] },
    lodgements: apiLodgements || apiFund.lodgements || mockFund?.lodgements || [],
    auditPack: apiAudits || apiFund.audit_pack || apiFund.auditPack || mockFund?.auditPack || [],
  } : mockFund;

  if (fundLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading fund details...</span>
      </div>
    );
  }

  if (!fund) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <div className="text-center space-y-3">
          <h2 className="text-xl font-bold">Fund Not Found</h2>
          <p className="text-muted-foreground">The requested SMSF fund could not be found.</p>
          <Button variant="outline" onClick={() => navigate('/panel/smsf')}>
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to All Funds
          </Button>
        </div>
      </div>
    );
  }

  const compliance = COMPLIANCE_LABEL[fund.complianceStatus] || COMPLIANCE_LABEL['green'];
  const auditTotal = (fund.auditPack || []).reduce((s: number, c: any) => s + (c.items || 0), 0);
  const auditDone = (fund.auditPack || []).reduce((s: number, c: any) => s + (c.completed || 0), 0);
  const auditPct = auditTotal > 0 ? Math.round((auditDone / auditTotal) * 100) : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Shield}
        title={fund.name}
        description={`ABN ${fund.abn} | Established ${fund.establishmentDate} | ${fund.trusteeType} Trustee`}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/smsf')}>
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            All Funds
          </Button>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="trustees">Trustees</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="trust-deed">Trust Deed</TabsTrigger>
          <TabsTrigger value="bdbn">Death Benefits</TabsTrigger>
          <TabsTrigger value="investment">Investment Strategy</TabsTrigger>
          <TabsTrigger value="lodgement">Lodgement</TabsTrigger>
          <TabsTrigger value="audit-pack">Audit Pack</TabsTrigger>
        </TabsList>

        {/* ==================== Overview Tab ==================== */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                    <DollarSign className="h-4.5 w-4.5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Assets</p>
                    <p className="text-xl font-bold">${fund.totalAssets.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-blue-500/10 text-blue-600 flex items-center justify-center">
                    <Users className="h-4.5 w-4.5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Members</p>
                    <p className="text-xl font-bold">{(fund.members || []).length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-[#5048e5]/10 text-[#5048e5] flex items-center justify-center">
                    <TrendingUp className="h-4.5 w-4.5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Contributions YTD</p>
                    <p className="text-xl font-bold">${fund.contributionsYTD.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center',
                    fund.complianceStatus === 'green' ? 'bg-green-500/10 text-green-600' :
                    fund.complianceStatus === 'amber' ? 'bg-amber-500/10 text-amber-600' :
                    'bg-red-500/10 text-red-600'
                  )}>
                    <Shield className="h-4.5 w-4.5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Compliance</p>
                    <div className="flex items-center gap-1.5">
                      <span className={cn('h-2.5 w-2.5 rounded-full', compliance.color)} />
                      <p className="text-sm font-semibold">{compliance.label}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Fund Information</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-muted-foreground">Fund Name</dt><dd className="font-medium">{fund.name}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">ABN</dt><dd className="font-mono">{fund.abn}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">TFN</dt><dd className="font-mono">{fund.tfn}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Established</dt><dd>{fund.establishmentDate}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Trustee Type</dt><dd>{fund.trusteeType}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Trust Deed</dt><dd>{fund.trustDeed?.currentVersion} ({fund.trustDeed?.provider})</dd></div>
                </dl>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Quick Status</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-muted-foreground">Audit Pack</dt><dd className="font-medium">{auditDone}/{auditTotal} items ({auditPct}%)</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Next SAR Due</dt><dd>{fund.lodgements[0]?.dueDate}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">SAR Status</dt><dd><Badge variant={LODGEMENT_BADGE[fund.lodgements[0]?.status]?.variant}>{LODGEMENT_BADGE[fund.lodgements[0]?.status]?.label}</Badge></dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Deed Review Due</dt><dd>{fund.trustDeed?.reviewDue}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Investment Review</dt><dd>{fund.investmentStrategy?.nextReview}</dd></div>
                </dl>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ==================== Trustees Tab ==================== */}
        <TabsContent value="trustees" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Trustees & Directors</CardTitle>
              <CardDescription>Current trustee structure for this fund</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Name</th>
                      <th className="pb-2 pr-4 font-medium">Type</th>
                      <th className="pb-2 pr-4 font-medium">Role</th>
                      <th className="pb-2 pr-4 font-medium">Date Appointed</th>
                      <th className="pb-2 font-medium">Declaration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(fund.trustees || []).map((t: any, i: number) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-3 pr-4 font-medium">{t.name}</td>
                        <td className="py-3 pr-4">{t.type}</td>
                        <td className="py-3 pr-4">{t.role}</td>
                        <td className="py-3 pr-4 text-muted-foreground">{t.dateAppointed}</td>
                        <td className="py-3">
                          <Badge variant={t.declarationStatus === 'Current' ? 'default' : 'destructive'}>
                            {t.declarationStatus}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== Members Tab ==================== */}
        <TabsContent value="members" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Fund Members</CardTitle>
              <CardDescription>Member details and balances</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Name</th>
                      <th className="pb-2 pr-4 font-medium">Date of Birth</th>
                      <th className="pb-2 pr-4 font-medium">TFN</th>
                      <th className="pb-2 pr-4 font-medium text-center">Preservation Age</th>
                      <th className="pb-2 pr-4 font-medium">Status</th>
                      <th className="pb-2 font-medium text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(fund.members || []).map((m: any, i: number) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-3 pr-4 font-medium">{m.name}</td>
                        <td className="py-3 pr-4 text-muted-foreground">{m.dob}</td>
                        <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">{m.tfn}</td>
                        <td className="py-3 pr-4 text-center">{m.preservationAge}</td>
                        <td className="py-3 pr-4">
                          <Badge variant={m.status === 'Pension' ? 'default' : 'secondary'}>{m.status}</Badge>
                        </td>
                        <td className="py-3 text-right font-medium">${m.balance.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== Trust Deed Tab ==================== */}
        <TabsContent value="trust-deed" className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Current Trust Deed</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-muted-foreground">Version</dt><dd className="font-medium">{fund.trustDeed?.currentVersion}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Last Updated</dt><dd>{fund.trustDeed?.lastUpdated}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Provider</dt><dd>{fund.trustDeed?.provider}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Review Due</dt><dd className="font-medium">{fund.trustDeed?.reviewDue}</dd></div>
                </dl>
                <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => toast.info('Upload functionality coming soon')}>
                  <Upload className="h-4 w-4 mr-1.5" />
                  Upload New Version
                </Button>
              </CardContent>
            </Card>

            {/* Review due reminder */}
            {fund.trustDeed?.reviewDue && (
              <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-sm">Trust Deed Review Reminder</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        The trust deed is due for review by <span className="font-medium">{fund.trustDeed?.reviewDue}</span>.
                        Ensure the deed complies with current SIS Act requirements and ATO guidance.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Version History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(fund.trustDeed?.history || []).map((h: any, i: number) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                      <FileText className="h-3 w-3" />
                    </div>
                    <div>
                      <p className="font-medium">{h.version} - {h.date}</p>
                      <p className="text-muted-foreground">{h.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== BDBN (Death Benefits) Tab ==================== */}
        <TabsContent value="bdbn" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Binding Death Benefit Nominations (BDBN)</CardTitle>
              <CardDescription>Track nomination status and expiry dates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Member</th>
                      <th className="pb-2 pr-4 font-medium">Type</th>
                      <th className="pb-2 pr-4 font-medium">Beneficiaries</th>
                      <th className="pb-2 pr-4 font-medium">Date Signed</th>
                      <th className="pb-2 pr-4 font-medium">Expiry</th>
                      <th className="pb-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(fund.bdbn || []).map((b: any, i: number) => {
                      let statusColor = '';
                      let statusLabel = '';
                      if (b.daysUntilExpiry === null) {
                        statusColor = 'text-muted-foreground';
                        statusLabel = b.type === 'None' ? 'No Nomination' : 'No Expiry';
                      } else if (b.daysUntilExpiry <= 0) {
                        statusColor = 'text-red-600';
                        statusLabel = 'Expired';
                      } else if (b.daysUntilExpiry <= 90) {
                        statusColor = 'text-red-600';
                        statusLabel = `${b.daysUntilExpiry} days`;
                      } else if (b.daysUntilExpiry <= 180) {
                        statusColor = 'text-amber-600';
                        statusLabel = `${b.daysUntilExpiry} days`;
                      } else {
                        statusColor = 'text-green-600';
                        statusLabel = `${b.daysUntilExpiry} days`;
                      }

                      let rowBg = '';
                      if (b.daysUntilExpiry !== null) {
                        if (b.daysUntilExpiry <= 0) rowBg = 'bg-red-50 dark:bg-red-950/20';
                        else if (b.daysUntilExpiry <= 90) rowBg = 'bg-red-50/50 dark:bg-red-950/10';
                        else if (b.daysUntilExpiry <= 180) rowBg = 'bg-amber-50/50 dark:bg-amber-950/10';
                      }

                      return (
                        <tr key={i} className={cn('border-b last:border-0', rowBg)}>
                          <td className="py-3 pr-4 font-medium">{b.member}</td>
                          <td className="py-3 pr-4">
                            <Badge variant={b.type === 'Binding' ? 'default' : b.type === 'Non-Binding' ? 'secondary' : 'outline'}>{b.type}</Badge>
                          </td>
                          <td className="py-3 pr-4 text-muted-foreground">{b.beneficiaries}</td>
                          <td className="py-3 pr-4 text-muted-foreground">{b.dateSigned}</td>
                          <td className="py-3 pr-4 text-muted-foreground">{b.expiry}</td>
                          <td className={cn('py-3 font-medium', statusColor)}>
                            {b.daysUntilExpiry !== null && b.daysUntilExpiry <= 0 ? (
                              <span className="flex items-center gap-1"><XCircle className="h-3.5 w-3.5" /> {statusLabel}</span>
                            ) : b.daysUntilExpiry !== null && b.daysUntilExpiry <= 90 ? (
                              <span className="flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> {statusLabel}</span>
                            ) : (
                              statusLabel
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== Investment Strategy Tab ==================== */}
        <TabsContent value="investment" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Investment Strategy Summary</CardTitle>
              <CardDescription>
                Last reviewed: {fund.investmentStrategy?.lastReviewed} | Next review due: {fund.investmentStrategy?.nextReview}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Annual review alert */}
              {fund.investmentStrategy?.nextReview && (
                <div className="flex items-start gap-3 mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                  <Calendar className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    Annual investment strategy review is due by <span className="font-medium">{fund.investmentStrategy?.nextReview}</span>.
                    Ensure asset allocations align with the documented strategy.
                  </p>
                </div>
              )}

              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={fund.investmentStrategy?.allocations || []}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="asset" tick={{ fontSize: 12 }} interval={0} angle={-15} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 12 }} unit="%" />
                    <Tooltip formatter={(v) => `${v}%`} cursor={false} />
                    <Legend />
                    <Bar dataKey="target" name="Target %" fill="#5048e5" radius={[4, 4, 0, 0]} activeBar={false}/>
                    <Bar dataKey="actual" name="Actual %" fill="#10b981" radius={[4, 4, 0, 0]} activeBar={false}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== Lodgement Tab ==================== */}
        <TabsContent value="lodgement" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">SMSF Annual Return (SAR) Lodgements</CardTitle>
              <CardDescription>Annual return due dates and status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Financial Year</th>
                      <th className="pb-2 pr-4 font-medium">Due Date</th>
                      <th className="pb-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(fund.lodgements || []).map((l: any, i: number) => {
                      const badge = LODGEMENT_BADGE[l.status];
                      return (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{l.year}</td>
                          <td className="py-3 pr-4 text-muted-foreground">{l.dueDate}</td>
                          <td className="py-3">
                            <Badge variant={badge.variant}>{badge.label}</Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== Audit Pack Tab ==================== */}
        <TabsContent value="audit-pack" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Audit Pack Checklist</CardTitle>
                  <CardDescription>Document preparation progress for the current year audit</CardDescription>
                </div>
                <Button size="sm" onClick={() => toast.info('Audit pack generation coming soon')}>
                  <FolderOpen className="h-4 w-4 mr-1.5" />
                  Generate Pack
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Progress bar */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Overall Progress</span>
                  <span className="font-medium">{auditDone}/{auditTotal} items ({auditPct}%)</span>
                </div>
                <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      auditPct === 100 ? 'bg-green-500' : auditPct >= 60 ? 'bg-[#5048e5]' : 'bg-amber-500'
                    )}
                    style={{ width: `${auditPct}%` }}
                  />
                </div>
              </div>

              {/* Category checklist */}
              <div className="space-y-2">
                {(fund.auditPack || []).map((cat: any, i: number) => {
                  const pct = Math.round((cat.completed / cat.items) * 100);
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
                      <div className={cn(
                        'h-8 w-8 rounded-full flex items-center justify-center shrink-0',
                        pct === 100 ? 'bg-green-100 text-green-600 dark:bg-green-900/30' : 'bg-muted text-muted-foreground'
                      )}>
                        {pct === 100 ? <CheckCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">{cat.category}</p>
                          <p className="text-xs text-muted-foreground">{cat.completed}/{cat.items}</p>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-1.5">
                          <div
                            className={cn(
                              'h-full rounded-full',
                              pct === 100 ? 'bg-green-500' : pct > 0 ? 'bg-[#5048e5]' : 'bg-transparent'
                            )}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
